"use client";

import { X, ShoppingBag, Trash2, Plus, Minus, ArrowRight } from "lucide-react";
import Image from "next/image";
import { useSoftCart } from "./SoftCartContext";

export default function SoftCartDrawer({ subdomain }: { subdomain: string }) {
  const { cart, isCartOpen, closeCart, removeFromCart, updateQuantity, subtotal, totalItems } = useSoftCart();

  if (!isCartOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/40 backdrop-blur-sm animate-in fade-in">
      <div className="w-full max-w-md bg-[#F0F4F9] h-full shadow-2xl flex flex-col justify-between p-6 sm:p-8 relative">
        
        {/* Header */}
        <div className="flex items-center justify-between pb-4 border-b border-slate-200">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-2xl bg-blue-600 text-white flex items-center justify-center font-black">
              <ShoppingBag className="w-4 h-4" />
            </div>
            <div>
              <h3 className="text-sm font-black text-slate-900">Soft Store Cart</h3>
              <span className="text-[10px] text-slate-400">{totalItems} items selected</span>
            </div>
          </div>
          <button
            onClick={closeCart}
            className="w-9 h-9 rounded-2xl bg-white shadow-[4px_4px_10px_#dce5ef,-4px_-4px_10px_#ffffff] flex items-center justify-center text-slate-500 hover:text-slate-800 transition-colors cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Cart Items List */}
        <div className="flex-1 overflow-y-auto py-4 space-y-4 pr-1">
          {cart.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center space-y-3">
              <div className="w-16 h-16 rounded-3xl bg-white shadow-[8px_8px_16px_#dce5ef,-8px_-8px_16px_#ffffff] flex items-center justify-center text-slate-300">
                <ShoppingBag className="w-8 h-8" />
              </div>
              <p className="text-xs font-bold text-slate-500">Your Soft Cart is Empty</p>
            </div>
          ) : (
            cart.map((item) => (
              <div
                key={item.id}
                className="bg-white rounded-3xl p-4 shadow-[6px_6px_14px_#dce5ef,-6px_-6px_14px_#ffffff] border border-white flex items-center gap-4"
              >
                <div className="relative w-16 h-16 rounded-2xl bg-slate-50 overflow-hidden flex-shrink-0">
                  <Image
                    src={item.image || "https://images.unsplash.com/photo-1542838132-92c53300491e?w=200"}
                    alt={item.name}
                    fill
                    unoptimized
                    className="object-cover"
                  />
                </div>

                <div className="flex-1 min-w-0">
                  <h4 className="text-xs font-bold text-slate-900 truncate">{item.name}</h4>
                  <span className="text-xs font-black text-blue-600 font-mono block mt-0.5">
                    ৳{(item.price * item.quantity).toLocaleString()}
                  </span>

                  <div className="flex items-center gap-2 mt-2">
                    <button
                      onClick={() => updateQuantity(item.id, -1)}
                      className="w-7 h-7 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 flex items-center justify-center transition-colors cursor-pointer"
                    >
                      <Minus className="w-3 h-3" />
                    </button>
                    <span className="text-xs font-bold font-mono w-4 text-center">{item.quantity}</span>
                    <button
                      onClick={() => updateQuantity(item.id, 1)}
                      className="w-7 h-7 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 flex items-center justify-center transition-colors cursor-pointer"
                    >
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>
                </div>

                <button
                  onClick={() => removeFromCart(item.id)}
                  className="w-8 h-8 rounded-xl bg-rose-50 text-rose-600 hover:bg-rose-100 flex items-center justify-center transition-colors cursor-pointer"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))
          )}
        </div>

        {/* Footer Subtotal & Checkout */}
        <div className="pt-4 border-t border-slate-200 space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-500">Subtotal:</span>
            <span className="text-base font-black text-slate-900 font-mono">
              ৳{subtotal.toLocaleString()}
            </span>
          </div>

          <button
            type="button"
            onClick={() => alert("Proceeding to Soft Checkout...")}
            disabled={cart.length === 0}
            className="w-full py-3.5 rounded-full bg-blue-600 hover:bg-blue-500 text-white text-xs font-black shadow-lg shadow-blue-500/25 transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
          >
            <span>Proceed to Checkout</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>

      </div>
    </div>
  );
}